import { analyzeShortStrikeSafety } from '@/lib/positionSafety';
import { capitalHeld } from '@steward/shared';
import { getDaysToExpiration, toLocalDate } from '@/lib/formatters';
import type { LearnContext, LearnLegInput } from './types';

const isShort = (action: LearnLegInput['action']) => action === 'STO' || action === 'STC';
const isOpen = (leg: LearnLegInput) => leg.quantityRemaining > 0;

/**
 * Net premium per share, from a position's net premium in DOLLARS for all contracts.
 *
 * Lives here rather than on either surface because the Risk view and the chain detail must read
 * the same credit for the same position — a second, differently-derived basis on one of them is
 * two numbers for one trade.
 *
 * The assumption, stated because nothing in the data carries it: every leg of a position is traded
 * at the same contract count, so the position's size is the largest `quantityRemaining` among its
 * open legs (the largest, not the sum, because a spread's two legs are one contract's worth of risk
 * each). A partially closed leg leaves a smaller remainder than its sibling, so the max is the size
 * that is still working. Falls back to the ordered `quantity` when nothing remains, and returns
 * null rather than dividing by zero.
 */
export function perSharePremium(
  netPremiumDollars: number,
  legs: Array<{ quantityRemaining: number; quantity: number }>,
): number | null {
  const remaining = Math.max(0, ...legs.map((leg) => leg.quantityRemaining));
  const contracts = remaining > 0 ? remaining : Math.max(0, ...legs.map((leg) => leg.quantity));
  if (contracts <= 0) return null;
  return netPremiumDollars / (contracts * 100);
}

/** A quote that failed to parse is "no price", never a NaN that formats as "$NaN". */
export const finiteOrNull = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** A leg paired with its key into a Greeks map, so the lookup stays pure and testable. */
export interface DeltaLegInput {
  optionType: 'put' | 'call';
  action: LearnLegInput['action'];
  strikePrice: number;
  quantityRemaining: number;
  /** Null when the leg's option symbol could not be parsed into a quote key. */
  greeksKey: string | null;
}

/**
 * The live delta of the short put and the short call, as magnitudes (the probability read).
 *
 * It picks the SAME strikes `buildLearnContext` reports — the highest short put, the lowest short
 * call — so the delta sentence and the strike it names describe one leg, not two.
 */
export function shortLegDeltas(
  legs: DeltaLegInput[],
  greeks: Map<string, { delta: number | null }>,
): { shortPutDelta: number | null; shortCallDelta: number | null } {
  const forSide = (optionType: 'put' | 'call'): number | null => {
    const candidates = legs.filter(
      (leg) => leg.quantityRemaining > 0 && leg.optionType === optionType && isShort(leg.action),
    );
    if (candidates.length === 0) return null;
    const chosen = candidates.reduce((best, leg) =>
      optionType === 'put'
        ? leg.strikePrice > best.strikePrice
          ? leg
          : best
        : leg.strikePrice < best.strikePrice
          ? leg
          : best,
    );
    const delta = chosen.greeksKey == null ? null : greeks.get(chosen.greeksKey)?.delta;
    return typeof delta === 'number' && Number.isFinite(delta) ? Math.abs(delta) : null;
  };
  return { shortPutDelta: forSide('put'), shortCallDelta: forSide('call') };
}

export interface BuildLearnContextInput {
  symbol: string;
  legs: LearnLegInput[];
  price: number | null;
  netPremiumPerShare: number | null;
  shortPutDelta?: number | null;
  shortCallDelta?: number | null;
}

export function buildLearnContext(input: BuildLearnContextInput): LearnContext {
  const legs = input.legs.filter(isOpen);
  const strikes = (optionType: 'put' | 'call', short: boolean) =>
    legs
      .filter((leg) => leg.optionType === optionType && isShort(leg.action) === short)
      .map((leg) => leg.strikePrice);
  const max = (values: number[]) => (values.length ? Math.max(...values) : null);
  const min = (values: number[]) => (values.length ? Math.min(...values) : null);

  const held = capitalHeld(
    legs.map((leg) => ({
      optionType: leg.optionType,
      action: leg.action,
      strikePrice: leg.strikePrice,
      // capitalHeld groups on a YYYY-MM-DD day label; expirations are UTC-midnight instants.
      expiration: leg.expirationDate.toISOString().slice(0, 10),
      quantityRemaining: leg.quantityRemaining,
      pricePerContract: leg.pricePerContract,
    })),
  ).held;

  const nearest =
    legs
      .map((leg) => toLocalDate(leg.expirationDate))
      .sort((a, b) => a.getTime() - b.getTime())[0] ?? null;

  return {
    symbol: input.symbol,
    price: input.price,
    // The protective put sits below its short; the protective call sits above its short.
    shortPut: max(strikes('put', true)),
    longPut: min(strikes('put', false)),
    shortCall: min(strikes('call', true)),
    longCall: max(strikes('call', false)),
    credit: input.netPremiumPerShare,
    held,
    dte: nearest ? getDaysToExpiration(nearest) : null,
    safety: legs.length ? analyzeShortStrikeSafety(legs, input.price) : null,
    shortPutDelta: input.shortPutDelta ?? null,
    shortCallDelta: input.shortCallDelta ?? null,
  };
}
